import React, { useContext } from 'react';
import axios from 'axios';
import { Link, useHistory, useLocation } from 'react-router-dom';

import type { AuthContextType, UserFavoritesContextType } from 'contexts';
import {
  UniversalSearchContext,
  AuthContext,
  ChannelType,
  ContextualChatContext,
} from 'contexts';
import { useBreakpoint, useStartChat } from 'hooks';
import { AppExternalController } from 'api';
import {
  featureEnabled,
  isBrokerBayAdminEmail,
  isContextualChatEnabled,
} from 'utils';
import { Icon as NewIcon } from '@brokerbay/elements-web';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
// Avoid cycle dependency by import Modal straight from the source
import { Modal } from 'components/Modal';
import Icon from '../Icon';
import Button from '../Button';
import Space from '../Space';
import {
  StyledButton,
  StyledButtonWrapper,
  StyledDropdown,
  NoWrapRow,
  StyledModalTitle,
  StyledModalContent,
  StyledModalButtonText,
} from './StyledComponents';

import { ColumnOptions } from './types';
import { addListingToHistory } from '../../api/RecentSearchHistoryController';
import TogglableButtonWithIcon from '../Button/TogglableButtonWithIcon';
import ScheduleButtonDropdown, {
  ActionTooltip,
} from './ScheduleButtonDropdown';
import ListingSummaryProps from '../../types/listingSummaryPropsType';

type ListingRowActionsProps = {
  id: string;
  forceBlur: () => void;
  favorites?: UserFavoritesContextType['favorites'];
  toggleFavorite?: UserFavoritesContextType['toggleFavorite'];
  acceptsRegisteredOffers?: boolean;
  chatAgents?: string[];
  showScheduleBtn?: boolean;
  searchFromTour?: boolean;
  openTourListingModal?: (listingId: string) => void;
  index: number;
  options: ColumnOptions;
  hasFeature: AuthContextType['hasFeature'];
  listing: ListingSummaryProps;
  noMoreShowings: boolean;
  external?: boolean;
};

const ListingRowActions: React.FC<ListingRowActionsProps> = ({
  id,
  forceBlur,
  favorites,
  toggleFavorite,
  acceptsRegisteredOffers,
  chatAgents,
  showScheduleBtn,
  searchFromTour,
  openTourListingModal,
  index,
  options,
  hasFeature,
  listing,
  noMoreShowings,
  external,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { activeRow } = useContext(UniversalSearchContext);

  const { md } = useBreakpoint();
  const { auth } = useContext(AuthContext);
  const { setChatModalVisible } = useContext(ContextualChatContext);
  const { startChat } = useStartChat();

  // action column buttons
  const handleViewListingClick = async () => {
    // For the "View Listing" button for unclaimed listings not in Tours search
    if (external) {
      const { pathname } = location;
      const [, identifier, externalId] = pathname.split('/');
      const newExternalId =
        await AppExternalController.createNewDashboardForListing(
          identifier,
          externalId,
          id,
        );
      history.push(`/${identifier}/${newExternalId}/listing/`);
    } else {
      addListingToHistory(id);
      history.push(`/listing/${listing.id}/view`);
    }
  };
  const handleScheduleClick = async (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    if (featureEnabled('preBookingNotificationFeature')) {
      if (external) {
        const { pathname } = location;
        const [, identifier, externalId] = pathname.split('/');
        const newExternalId =
          await AppExternalController.createNewDashboardForListing(
            identifier,
            externalId,
            id,
          );

        if (hasFeature('PRE_BOOKING') && listing.preBookingInformation) {
          Modal.confirm({
            icon: null,
            title: <StyledModalTitle>Important Information</StyledModalTitle>,
            content: (
              <StyledModalContent>
                {listing.preBookingInformation}
              </StyledModalContent>
            ),
            okText: 'Continue to booking',
            cancelText: 'Cancel',
            closable: true,
            closeIcon: <Icon type="antdCloseOutlined" width={16} />,
            zIndex: 4000,
            onOk() {
              forceBlur();
              history.push(
                `/${identifier}/${newExternalId}/${
                  identifier === 'rets' ? 'appointments/book' : 'book'
                }`,
              );
            },
            getContainer: e.currentTarget,
          });
          return;
        }

        history.push(
          `/${identifier}/${newExternalId}/${
            identifier === 'rets' ? 'appointments/book' : 'book'
          }`,
        );
      } else if (hasFeature('PRE_BOOKING') && listing.preBookingInformation) {
        Modal.confirm({
          icon: null,
          title: <StyledModalTitle>Important Information</StyledModalTitle>,
          content: (
            <StyledModalContent>
              {listing.preBookingInformation}
            </StyledModalContent>
          ),
          okText: (
            <StyledModalButtonText>Continue to booking</StyledModalButtonText>
          ),
          cancelText: <StyledModalButtonText>Cancel</StyledModalButtonText>,
          closable: true,
          closeIcon: <Icon type="antdCloseOutlined" width={16} />,
          onOk() {
            forceBlur();
            addListingToHistory(id);
            history.push(`/listing/${id}/appointments/book`);
          },
          getContainer: e.currentTarget,
        });
        return;
      } else {
        addListingToHistory(id);
        history.push(`/listing/${id}/appointments/book`);
      }

      forceBlur();
      return;
    }

    if (external) {
      const { pathname } = location;
      const [, identifier, externalId] = pathname.split('/');
      const newExternalId =
        await AppExternalController.createNewDashboardForListing(
          identifier,
          externalId,
          id,
        );
      history.push(
        `/${identifier}/${newExternalId}/${
          identifier === 'rets' ? 'appointments/book' : 'book'
        }`,
      );
    } else {
      addListingToHistory(id);
      history.push(`/listing/${id}/appointments/book`);
    }
    forceBlur();
  };
  const handleMessageClick = () => (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    addListingToHistory(id);
    axios.post('/messaging/newConvo', {
      userIds: chatAgents,
    });
  };
  const handleRegisterOfferClick = async (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    if (external) {
      const { pathname } = location;
      const [, identifier, externalId] = pathname.split('/');
      const newExternalId =
        await AppExternalController.createNewDashboardForListing(
          identifier,
          externalId,
          id,
        );
      history.push(
        `/${identifier}/${newExternalId}/${
          identifier === 'rets' ? 'appointments/offer' : 'offers'
        }`,
      );
    } else {
      addListingToHistory(id);
      history.push(`/listing/${id}/offer/new`);
    }
    forceBlur();
  };
  const handleOnFavoriteClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    addListingToHistory(id, true);
    toggleFavorite(id);
  };
  const handleOnClickLink =
    (listingId: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation();
      addListingToHistory(listingId);
      forceBlur();
    };

  const menuProps: MenuItemType[] = [
    options?.showChatBtn &&
      !listing.org?.isUnclaimed &&
      !isContextualChatEnabled(auth?.org.mlsTypes) && {
        key: 'contact',
        icon: <NewIcon icon="messageOutlined" />,
        label: 'Contact Agent',
        disabled: !chatAgents?.length,
        onClick: () => {
          addListingToHistory(id);
          axios.post('/messaging/newConvo', {
            userIds: chatAgents,
          });
        },
      },
    options?.showChatBtn &&
      !listing.org?.isUnclaimed &&
      isContextualChatEnabled(auth?.org.mlsTypes) && {
        key: 'contact',
        icon: <NewIcon icon="messageOutlined" />,
        label: `Message Agent${chatAgents.length > 1 ? 's' : ''}`,
        disabled: !chatAgents?.length,
        onClick: () => {
          addListingToHistory(id);
          setChatModalVisible(true);
          startChat({
            userId: auth.streamChatId,
            selectedUsers: chatAgents.map(repId => ({ id: repId })),
            listingId: id,
            channelType: ChannelType.listing,
          });
        },
      },
    favorites &&
      toggleFavorite && {
        key: 'favorites',
        icon: <NewIcon icon="heartOutlined" />,
        label: 'Add to Favorites',
        disabled: !toggleFavorite || external,
        onClick: () => {
          addListingToHistory(id, true);
          toggleFavorite(id);
        },
      },
    // Temporarily disabled for [SUPRANN-161]
    // {
    //   key: 'share',
    //   icon: <NewIcon icon="sendOutlined" />,
    //   label: 'Share Listing',
    //   disabled: external,
    //   onClick: () => {
    //     addListingToHistory(id);
    //     history.push(`/listing/send/${id}`);
    //     forceBlur();
    //   },
    // },
    hasFeature('OFFERS') &&
      !listing.org?.isUnclaimed && {
        key: 'register',
        icon: <NewIcon icon="editOutlined" />,
        label: 'Register Offer',
        disabled: !acceptsRegisteredOffers,
        onClick: async () => {
          addListingToHistory(id);
          history.push(`/listing/${id}/offer/new`);
          forceBlur();
        },
      },
  ].filter(Boolean) as MenuItemType[];

  if (
    activeRow !== index &&
    md &&
    !(featureEnabled('showingsMadeEasyFeature') && !external)
  )
    return null;

  return (
    <>
      {searchFromTour ? (
        <Space>
          <StyledButtonWrapper disabled={false} searchFromTour md={md}>
            <StyledButton
              icon={<Icon type="eye" color="black" offset={[0, 2]} />}
              data-cy="listing-view-button"
              $searchFromTour
              onClick={e => {
                e.stopPropagation();
                openTourListingModal(id);
              }}
            >
              {md && 'View Listing'}
            </StyledButton>
          </StyledButtonWrapper>
        </Space>
      ) : listing.org?.isUnclaimed &&
        !(featureEnabled('showingsMadeEasyFeature') && !external) &&
        // Answering Service Users, MLS Staff Users & BrokerBay Admin users can see/take any action
        (external ||
          !auth.assumingUserId ||
          (auth.assumingUserId &&
            !auth.isAssumedNotBB &&
            !isBrokerBayAdminEmail(auth.email))) ? (
        <Space>
          <StyledButton
            data-cy="view-listing-button"
            onClick={handleViewListingClick}
            icon={<Icon type="antdHomeFilled" width={16} />}
          >
            View Listing
          </StyledButton>
        </Space>
      ) : (
        <NoWrapRow onClick={e => e.stopPropagation()} external>
          <Space>
            <ScheduleButtonDropdown
              handleScheduleClick={handleScheduleClick}
              showScheduleBtn={showScheduleBtn}
              listing={listing}
              noMoreShowings={noMoreShowings}
              external={external}
            />
            {options.lg &&
              (featureEnabled('showingsMadeEasyFeature') && !external ? (
                <StyledDropdown
                  menu={{ items: menuProps }}
                  trigger={['click']}
                  getPopupContainer={trigger => trigger}
                >
                  <button>
                    <NewIcon icon="moreOutlined" size={16} />
                  </button>
                </StyledDropdown>
              ) : (
                <>
                  {options.showChatBtn && (
                    <ActionTooltip
                      title={
                        chatAgents?.length
                          ? 'Message agent(s)'
                          : 'Online chat is not currently available for the listing agent(s).'
                      }
                      data-cy="message-agent-tooltip"
                    >
                      <StyledButtonWrapper disabled={!chatAgents?.length}>
                        <StyledButton
                          onClick={handleMessageClick}
                          icon={<Icon type="chatBubble" width={16} />}
                          shape="circle"
                          disabled={!chatAgents?.length}
                          data-cy="message-agent-button"
                        />
                      </StyledButtonWrapper>
                    </ActionTooltip>
                  )}
                  {hasFeature('OFFERS') && (
                    <ActionTooltip
                      data-cy="register-offer-tooltip"
                      title={
                        acceptsRegisteredOffers
                          ? 'Register Offer'
                          : 'Online offer registration is not currently available for this listing.'
                      }
                    >
                      <StyledButtonWrapper disabled={!acceptsRegisteredOffers}>
                        <StyledButton
                          data-cy="register-offer-button"
                          disabled={!acceptsRegisteredOffers}
                          onClick={handleRegisterOfferClick}
                          shape="circle"
                          icon={<Icon type="pencilFilled" width={16} />}
                        />
                      </StyledButtonWrapper>
                    </ActionTooltip>
                  )}
                  {favorites && toggleFavorite && !external && (
                    <ActionTooltip title="Favorite" data-cy="favorite-tooltip">
                      <TogglableButtonWithIcon
                        onClick={handleOnFavoriteClick}
                        isToggled={favorites.has(id)}
                        iconType="favorite"
                        iconWidth={16}
                        iconOffset={[0, 3]}
                        shape="circle"
                        data-cy="favorite-button"
                      />
                    </ActionTooltip>
                  )}
                  {/* Temporarily disabled for [SUPRANN-161] */}
                  {/* {!external && (
                    <ActionTooltip title="Share" data-cy="share-tooltip">
                      <div>
                        <Link
                          to={`/listing/send/${id}`}
                          onClick={handleOnClickLink(id)}
                        >
                          <Button
                            icon={<Icon type="share" width={16} />}
                            shape="circle"
                            data-cy="share-button"
                          />
                        </Link>
                      </div>
                    </ActionTooltip>
                  )} */}
                </>
              ))}
          </Space>
        </NoWrapRow>
      )}
    </>
  );
};

export default ListingRowActions;
